import { Link } from 'gatsby'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React, { FC, useMemo, useRef } from 'react'
import CloseRounded from 'src/images/svgs/icons/CloseRounded.svg'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import { useDragToDismiss } from 'src/utils/useDragToDismiss'
import { useTranslation } from 'src/utils/useTranslation'
import * as Styles from './ctaPopup.module.scss'

/**
 * Maximum length for the headline of the CTA popup. If the headline exceeds this length, it will be truncated.
 */
const MAX_HEADLINE_LENGTH = 45

/**
 * Maximum length for the body text of the CTA popup. If the body text exceeds this length, it will be truncated.
 */
const MAX_BODY_LENGTH = 320

/**
 * The scroll threshold in pixels at which the CTA popup will be dismissed when the user drags it downward.
 */
const DISMISS_SCROLL_THRESHOLD_PX = 100

/**
 * Enum for the image mode of the CTA popup as received from Prismic.
 */
enum ImageMode {
  /**
   * The image is displayed as a background of the popup.
   */
  AS_BACKGROUND = 'As background',
  /**
   * The image is displayed above the headline of the popup as an inline image.
   */
  ABOVE_HEADLINE = 'Above headline',
}

/**
 * Props for the `CtaPopup` component.
 */
export type CtaPopupProps = {
  ctaPopupId: string
  headline?: string | null
  body?: string | null
  image?: {
    data: IGatsbyImageData
    alt: string
  } | null
  imageMode?: string | null
  ctaLabel?: string | null
  ctaLink?: PrismicLinkType | null
  rejectButtonLabel?: string | null
  onClose: () => void
}

/**
 * Component that renders a popup with an image, headline, body text, and call-to-action button.
 */
export const CtaPopup: FC<CtaPopupProps> = props => {
  const {
    ctaPopupId,
    headline,
    body,
    image,
    imageMode = ImageMode.AS_BACKGROUND,
    ctaLabel,
    ctaLink,
    rejectButtonLabel,
    onClose,
  } = props

  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)

  useDragToDismiss(containerRef, DISMISS_SCROLL_THRESHOLD_PX, onClose)

  const headlineElementId = headline
    ? `cta-popup-headline-${ctaPopupId}`
    : undefined
  const bodyElementId = body ? `cta-popup-body-${ctaPopupId}` : undefined

  /**
   * Memoized element for the background image of the popup, if the image mode is set to "As background" and an image
   * is provided.
   */
  const backgroundImageElement = useMemo(() => {
    if (imageMode === ImageMode.AS_BACKGROUND && image?.data) {
      return (
        <div className={Styles.backgroundImageContainer}>
          <GatsbyImage
            image={image.data}
            alt={image.alt ?? ''}
            className={Styles.backgroundImage}
          />
        </div>
      )
    }
    return null
  }, [image, imageMode])

  const closeButtonElement = useMemo(() => {
    return (
      <button
        type="button"
        className={`${Styles.closeButton} ${
          image?.data && imageMode === ImageMode.AS_BACKGROUND
            ? Styles.closeButtonWithBackgroundImage
            : ''
        }`}
        aria-label={t('components.CtaPopup.closeButtonAriaLabel')}
        onClick={onClose}
      >
        <CloseRounded />
      </button>
    )
  }, [onClose, image, imageMode, t])

  /**
   * Memoized element for the inline image of the popup, if the image mode is set to "Above headline" and an image
   * is provided.
   */
  const inlineImageElement = useMemo(() => {
    if (imageMode === ImageMode.ABOVE_HEADLINE && image?.data) {
      return (
        <GatsbyImage
          className={Styles.inlineImage}
          image={image.data}
          alt={image.alt ?? ''}
        />
      )
    }
    return null
  }, [image, imageMode])

  /**
   * Memoized element for the headline of the popup, if a headline is provided.
   */
  const headlineElement = useMemo(() => {
    if (headline) {
      const truncatedHeadline = headline.slice(0, MAX_HEADLINE_LENGTH)
      return (
        <span className={Styles.headline} id={headlineElementId}>
          {truncatedHeadline}
        </span>
      )
    }
    return null
  }, [headline, headlineElementId])

  /**
   * Memoized element for the body text of the popup, if body text is provided.
   */
  const bodyElement = useMemo(() => {
    if (body) {
      const truncatedBody = body.slice(0, MAX_BODY_LENGTH)
      return (
        <p className={Styles.body} id={bodyElementId}>
          {truncatedBody}
        </p>
      )
    }
    return null
  }, [body, bodyElementId])

  /**
   * Memoized element for the actions of the popup, which includes the call-to-action link and the reject button, if
   * they are provided.
   */
  const actionsElement = useMemo(() => {
    if (ctaLabel && ctaLink) {
      return (
        <div className={Styles.actions}>
          <Link to={ctaLink?.url ?? '#'} className={Styles.ctaLink}>
            {ctaLabel}
          </Link>
          {rejectButtonLabel && (
            <button
              type="button"
              className={Styles.rejectButton}
              onClick={onClose}
            >
              {rejectButtonLabel}
            </button>
          )}
        </div>
      )
    }
    return null
  }, [ctaLabel, ctaLink, onClose, rejectButtonLabel])

  // If the `ctaPopupId` is not provided, return null to avoid rendering the popup.
  if (!ctaPopupId) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={Styles.ctaPopupContainer}
      role="dialog"
      aria-modal="false"
      aria-labelledby={headlineElementId}
      aria-describedby={bodyElementId}
    >
      {backgroundImageElement}

      <div
        className={`${Styles.ctaPopupContent} ${
          image?.data && imageMode === ImageMode.AS_BACKGROUND
            ? Styles.ctaPopupContentWithBackgroundImage
            : ''
        }`}
      >
        {closeButtonElement}

        {inlineImageElement}

        {headlineElement}

        {bodyElement}

        {actionsElement}
      </div>
    </div>
  )
}
